// @ts-strict-ignore
import partition from 'lodash/partition';

import {SectionItem, SubLabelTheme} from 'common/components/items-menu/types';
import {getIconForPropertyType} from 'common/report/util/properties';
import {PropertyDefinition} from 'common/util/definitions-types';
import {FeatureGate, Permission, ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {Cohort as CohortModel} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {MetricsResultsMap} from 'app_api/projects/metrics/types';
import {ActionType, ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForCohort, getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {CohortStore} from 'common/report/stores/cohorts';
import {
  SerializedCohort,
  SerializedInlineCohort,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {CurrentProject} from 'common/report/model-resources/projects';
import {
  getAllPropertyDefinitionsForGroupProfiles,
  getAllPropertyDefinitionsForUserProfiles,
} from 'common/report/stores/property-definitions';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as trackShare,
} from 'common/widgets/sharing/tracking';
import {openEventsSidebar} from 'common/widgets/events-sidebar';
import {ContextPanelReactProps, EditDescriptionCustomEvent} from 'common/widgets/context-panel';
import {promptSharing} from 'common/widgets/sharing';
import {EntityType, EntityTypeNameSingular, SharableEntityType} from 'common/report/util/saved-entities/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {showMetricsApiErrorToast, updateMetric} from 'common/report/queries/app-api/metrics';
import {PATH_SEPARATOR} from 'common/constants';
import {MetricType} from 'common/types/reports/bookmark';
import {LocalizationStore} from 'common/i18n/store';
import {generateSavedMetricContextPanelData} from 'common/query-builder/saved-metrics-menu/context-panel';
import {InlineCohortsModalProps} from 'common/widgets/inline-cohorts-modal/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {BehaviorTypeIcons} from 'common/report/insights/util/metrics';
import {ProjectUsersResource} from 'common/report/model-resources/project-users';
import {ReplayPreviewRendererCallback} from 'common/widgets/replay-preview/types';

import {ChildMenuItemsCache, ContextPanelRendererParams} from '../child-menu';
import {NUM_RECENT_ITEMS, TRACKING_EVENTS} from '../constants';
import {CohortItem, isScdProperty} from '../properties-menu/types';
import {InlineCohortSegment} from '../segment-menu/types';
import {EventsController} from '../contexts/events-controller';
import {isEventItem} from '../events-menu/util';
import {generateBehaviorContextPanelData} from '../behaviors-menu/context-panel';
import {generateFormulaContextPanelData} from '../formulas-menu/context-panel';
import {getDataDefinitionsPropertyMenuItem, transformCohortToCohortItem} from '../properties-menu/util';
import {updateEntityDescription} from '../utils/util';
import {
  CohortItemValue,
  CohortSectionItem,
  isCohortMenuSectionItem,
  isEventMenuSectionItem,
  isFormulaMenuSectionItem,
  isFunnelMenuSectionItem,
  isProfileMenuSectionItem,
  isRetentionMenuSectionItem,
  isSavedMetricMenuSectionItem,
  isSimpleMenuSectionItem,
  MenuType,
  MenuTypeMap,
  MenuTypeToMetricType,
  MultiMetricsSection,
  MultiMetricsSectionItem,
  ProfileItemValue,
  SidebarCategory,
} from './types';

export function getRecentItems<T extends MenuType>(cache: ChildMenuItemsCache, menuType: T): Array<MenuTypeMap[T]> {
  return cache.getRecentItems(menuType, NUM_RECENT_ITEMS);
}

export function getProfileSections({
  dataGroupIdToNameMap,
  metricLevelDataGroups,
  historicalProperties,
}: {
  dataGroupIdToNameMap: {[id: string]: string};
  metricLevelDataGroups: boolean;
  historicalProperties: {[id: string]: PropertyDefinition[]};
}): Array<MultiMetricsSection<ProfileItemValue>> {
  const iconMap = BehaviorTypeIcons;
  let profileItems = [
    {
      icon: iconMap[MetricType.People],
      label: ({t}) => t(`query-builder.multi-metrics-menu.analyze-profiles`),
      menuType: MenuType.Profile,
      value: `$all_people` as const,
    },
  ];
  if (metricLevelDataGroups) {
    profileItems = Object.entries(dataGroupIdToNameMap).map(([dataGroupId, dataGroupName]) => {
      return {
        icon: iconMap[MetricType.People],
        label: ({t}) => `${dataGroupName} ${t(`mixology.profile_plural`)}`,
        menuType: MenuType.Profile,
        value: `$all_people`,
        ...(dataGroupId ? {profileDataGroupId: dataGroupId} : {}),
      };
    });
  }
  const sections: Array<MultiMetricsSection<ProfileItemValue>> = [
    {
      label: ({t}) => t(`query-builder.multi-metrics-menu.section-profile`),
      items: profileItems,
    },
  ];
  for (const [dataGroupId, histProps] of Object.entries(historicalProperties)) {
    const dataGroupName = dataGroupIdToNameMap[dataGroupId];
    const labelSuffix = metricLevelDataGroups ? ` - ${dataGroupName}` : ``;
    if (histProps.length > 0) {
      sections.push({
        label: ({t}) => `${t(`query-builder.multi-metrics-menu.section-historical-profile-props`)}${labelSuffix}`,
        items: histProps.map((property) => {
          return {
            icon: getIconForPropertyType(property.type, {isScd: true}),
            label: property.label,
            subLabel: `${dataGroupName}${PATH_SEPARATOR}`,
            subLabelTheme: SubLabelTheme.Breadcrumb,
            menuType: MenuType.Profile,
            value: {
              resourceType: ResourceType.User,
              scdEvent: property.scdEvent,
              dataGroupId: property.dataGroupId,
              propertyDefaultType: property.type,
              type: property.type,
              value: property.name,
            },
          };
        }),
      });
    }
  }

  return sections;
}

export function getCohortSections({
  recentCohortSectionItems,
  dataGroupIdToNameMap,
  userId,
  cohortsByDataGroupId,
  metricLevelDataGroups,
}: {
  recentCohortSectionItems: CohortSectionItem[];
  dataGroupIdToNameMap: {[id: string]: string};
  userId: string | number;
  cohortsByDataGroupId: {[id: string]: CohortItem[]};
  metricLevelDataGroups: boolean;
}): Array<MultiMetricsSection<CohortItemValue>> {
  const getMenuItem = ({
    cohort,
    dataGroupId,
    dataGroupName,
    multipleDataGroups,
  }: {
    cohort: CohortItem;
    dataGroupId: string;
    dataGroupName: string;
    multipleDataGroups: boolean;
  }): CohortSectionItem => {
    let cohortItemValue: CohortItemValue;
    if (typeof cohort.value === `number`) {
      cohortItemValue = {
        ...(dataGroupId ? {dataGroupId} : {}),
        resourceType: ResourceType.Cohort,
        label: cohort.label,
        value: cohort.value,
      };
    } else {
      cohortItemValue = {
        resourceType: ResourceType.Cohort,
        label: cohort.label,
        value: cohort.value as InlineCohortSegment,
      };
    }
    const label = multipleDataGroups ? `${dataGroupName}${PATH_SEPARATOR} ${cohort.label}` : cohort.label;
    return {
      icon: `community`,
      label,
      menuType: MenuType.Cohort,
      value: cohortItemValue,
    };
  };

  const multipleDataGroups = Object.keys(cohortsByDataGroupId).length > 1;
  const sections = Object.entries(cohortsByDataGroupId).map(([dataGroupId, cohorts]) => {
    const dataGroupName = dataGroupIdToNameMap[dataGroupId];
    let inlineCohorts: CohortItem[] = [];
    let [userCohorts, allCohorts] = partition(cohorts, (cohort) => cohort.createdBy && cohort.createdBy.id === userId);
    [inlineCohorts, userCohorts] = partition(userCohorts, (cohort) => {
      if (typeof cohort.value !== `number`) {
        return cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort;
      }
      return false;
    });
    allCohorts = allCohorts.filter((cohort) => cohort.canView);
    const labelPrefix = metricLevelDataGroups ? `${dataGroupName} ` : ``;
    return {
      label: ({t}) => `${labelPrefix}${t(`query-builder.multi-metrics-menu.section-cohorts`)}`,
      items: [...inlineCohorts, ...userCohorts, ...allCohorts].map((cohort) =>
        getMenuItem({cohort, dataGroupId, dataGroupName, multipleDataGroups}),
      ),
    };
  });

  return [
    {
      label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
      isRecentSection: true,
      items: recentCohortSectionItems,
    },
    ...sections,
  ].filter(Boolean);
}

export function getCreateMetricSectionItems({
  excludedMetricTypes,
  selectedCategory,
}: {
  excludedMetricTypes: Set<MetricType>;
  selectedCategory: SidebarCategory;
}): SectionItem[] {
  const unrestrictedCategories = [SidebarCategory.All, SidebarCategory.Verified, SidebarCategory.Metric];
  return [
    [...unrestrictedCategories, SidebarCategory.Simple].includes(selectedCategory) && {
      icon: `behavior-simple`,
      label: ({t}) => t(`query-builder.metrics.simple`),
      subLabel: ({t}) => t(`query-builder.multi-metrics-menu.create-new-description.simple`),
      value: MenuType.Simple,
    },
    [...unrestrictedCategories, SidebarCategory.Funnel].includes(selectedCategory) && {
      icon: `behavior-funnel`,
      label: ({t}) => t(`query-builder.metrics.funnel`),
      subLabel: ({t}) => t(`query-builder.multi-metrics-menu.create-new-description.funnel`),
      value: MenuType.Funnel,
    },
    [...unrestrictedCategories, SidebarCategory.Retention].includes(selectedCategory) && {
      icon: `behavior-retention`,
      label: ({t}) => t(`query-builder.metrics.retention`),
      subLabel: ({t}) => t(`query-builder.multi-metrics-menu.create-new-description.retention`),
      value: MenuType.Retention,
    },
    [...unrestrictedCategories, SidebarCategory.Cohort].includes(selectedCategory) && {
      icon: `brand-cohorts`,
      label: ({t}) => t(`query-builder.metrics.cohort`),
      subLabel: ({t}) => t(`query-builder.multi-metrics-menu.create-new-description.cohort`),
      value: MenuType.Cohort,
    },
    // NOTE: we don't show "create new profile" when in the profile category because "create new profile" just
    // sets the category to profile; so it doesn't make sense to show it if you're already in profile category
    [...unrestrictedCategories].includes(selectedCategory) && {
      icon: `group-attribute`,
      label: ({t}) => t(`query-builder.metrics.profile`),
      subLabel: ({t}) => t(`query-builder.multi-metrics-menu.create-new-description.profile`),
      value: MenuType.Profile,
    },
    !excludedMetricTypes.has(MetricType.CustomEvent) &&
      [...unrestrictedCategories, SidebarCategory.Event, SidebarCategory.CustomEvent].includes(selectedCategory) && {
        icon: `custom-event-beta`,
        label: ({t}) => t(`query-builder.metrics.custom-event`),
        subLabel: ({t}) => t(`query-builder.multi-metrics-menu.create-new-description.custom-event`),
        value: MenuType.Event,
      },
    [...unrestrictedCategories, SidebarCategory.Formula].includes(selectedCategory) && {
      icon: `formula`,
      label: ({t}) => t(`query-builder.metrics.formula`),
      subLabel: ({t}) => t(`query-builder.multi-metrics-menu.create-new-description.formula`),
      value: MenuType.Formula,
    },
  ].filter((item) => Boolean(item) && !excludedMetricTypes.has(MenuTypeToMetricType[item.value]));
}

export function getMultiMetricsItemValue(sectionItem: MultiMetricsSectionItem) {
  if (isEventMenuSectionItem(sectionItem) && isEventItem(sectionItem.value)) {
    return sectionItem.value.value;
  }
  if (isProfileMenuSectionItem(sectionItem)) {
    if (sectionItem.value !== `$all_people` && isScdProperty(sectionItem.value)) {
      return sectionItem.value.value;
    }
    return sectionItem.value;
  }
  if (isCohortMenuSectionItem(sectionItem)) {
    return sectionItem.value.label;
  }
  if (
    isFormulaMenuSectionItem(sectionItem) ||
    isFunnelMenuSectionItem(sectionItem) ||
    isRetentionMenuSectionItem(sectionItem)
  ) {
    return sectionItem.value.id;
  }
  return ``;
}

export function createInlineCohortModalAttrs({
  defaultCohort,
  dataGroupId,
  showDataGroupsSelector,
  userId,
  projectId,
  workspaceId,
  projectHasCohorts,
  supportB2bCohorts,
  trackName,
}: {
  defaultCohort: CohortModel;
  dataGroupId: string;
  showDataGroupsSelector: boolean;
  userId: number;
  projectId: number;
  workspaceId: number;
  projectHasCohorts: boolean;
  supportB2bCohorts: boolean;
  trackName: string;
}): InlineCohortsModalProps {
  return {
    canSave: projectHasCohorts,
    canEdit: isAllowedSuperadminOverride(defaultCohort) || defaultCohort.can_update_basic,
    dataGroupId: dataGroupId ?? null,
    showDataGroupsSelector,
    canApplyUnsavedCohort: !defaultCohort?.id,
    name: defaultCohort?.name,
    open: true,
    projectHasCohorts,
    supportB2bCohorts,
    projectId,
    cohort: defaultCohort?.serialize(),
    trackName,
    userId,
    workspaceId,
    showCohortCount: true,
  };
}

export function getHistoricalProperties(dataGroupId: string, i18n: LocalizationStore) {
  if (dataGroupId) {
    return getAllPropertyDefinitionsForGroupProfiles()
      .filter((property) => property.dataGroupId === dataGroupId && !!property.scdEvent)
      .map((property) => getDataDefinitionsPropertyMenuItem(property, i18n));
  }
  return getAllPropertyDefinitionsForUserProfiles()
    .filter((property) => property.status !== `custom` && !!property.scdEvent)
    .map((property) => getDataDefinitionsPropertyMenuItem(property, i18n));
}

export function getTooltipData(
  sectionItem: MultiMetricsSectionItem,
  {
    dataGroupName,
    readonly,
    descriptionEditDisabled,
    hideEditControls,
    eventsController,
    cohortsStore,
    currentProject,
    behaviors,
    metrics,
    i18n,
    projectUsers,
    dataGroups,
    replayPreviewRenderer,
  }: {
    dataGroupName: string;
    readonly: boolean;
    descriptionEditDisabled: boolean;
    hideEditControls: boolean;
    eventsController: EventsController;
    cohortsStore: CohortStore;
    currentProject: CurrentProject;
    behaviors: BehaviorsResource;
    metrics: MetricsResource;
    i18n: LocalizationStore;
    projectUsers: ProjectUsersResource;
    dataGroups: DataGroup[];
    replayPreviewRenderer?: ReplayPreviewRendererCallback;
  },
): ContextPanelData {
  if (!sectionItem || !sectionItem.value) {
    return null;
  }

  if (isEventMenuSectionItem(sectionItem)) {
    if (isEventItem(sectionItem.value)) {
      const tooltipData = eventsController.getContextPanelData(sectionItem.value, {
        allowEdit: !readonly && !hideEditControls,
        currentProject,
        projectUsers,
      });
      if (tooltipData && replayPreviewRenderer) {
        tooltipData.rowDetailsReplayPreviewRenderer = replayPreviewRenderer;
      }

      return tooltipData;
    }
    return null;
  } else if (isCohortMenuSectionItem(sectionItem)) {
    const cohortItemValue = sectionItem.value;
    let serializedCohort: SerializedCohort | SerializedInlineCohort | undefined;
    if (typeof cohortItemValue.value === `number`) {
      serializedCohort = cohortsStore.value[cohortItemValue.value];
    } else {
      const inlineCohortSegment: InlineCohortSegment = cohortItemValue.value;
      serializedCohort = cohortsStore.value[inlineCohortSegment.cohort.raw_cohort.unsavedId];
      if (!serializedCohort) {
        serializedCohort = inlineCohortSegment.cohort.raw_cohort;
      }
    }

    if (!serializedCohort) {
      return null;
    }
    const cohort = transformCohortToCohortItem(serializedCohort, cohortsStore);
    return getTooltipDataForCohort(cohort);
  } else if (
    isFunnelMenuSectionItem(sectionItem) ||
    isRetentionMenuSectionItem(sectionItem) ||
    isSimpleMenuSectionItem(sectionItem)
  ) {
    const canWriteBehaviors = currentProject.hasPermission(Permission.WriteBehaviors);
    const canShare = currentProject.getFeatureGate(FeatureGate.Sharing);
    return generateBehaviorContextPanelData({
      behaviorsCtx: behaviors,
      canShare,
      canWriteBehaviors,
      id: sectionItem.value.id,
    });
  } else if (isSavedMetricMenuSectionItem(sectionItem)) {
    const canWriteMetrics = currentProject.hasPermission(Permission.WriteMetrics);
    const canShare = currentProject.getFeatureGate(FeatureGate.Sharing);
    return generateSavedMetricContextPanelData({
      item: sectionItem.value,
      metricsContext: metrics,
      currentProject,
      behaviorsContext: behaviors,
      dataGroups,
      i18n,
      projectUsers,
      canWriteMetrics,
      canShare,
    });
  } else if (isFormulaMenuSectionItem(sectionItem)) {
    const canWriteMetrics = currentProject.hasPermission(Permission.WriteMetrics);
    const canShare = currentProject.getFeatureGate(FeatureGate.Sharing);
    return generateFormulaContextPanelData({
      id: sectionItem.value.id,
      metricsContext: metrics,
      currentProject,
      canWriteMetrics,
      canShare,
    });
  } else if (
    isProfileMenuSectionItem(sectionItem) &&
    sectionItem.value !== `$all_people` &&
    isScdProperty(sectionItem.value)
  ) {
    const property = sectionItem.value;
    const historicalProperties = getHistoricalProperties(property.dataGroupId, i18n);
    const tooltipData = getTooltipDataForProperty(
      {
        name: property.value as string,
        resourceType: property.resourceType,
        dataType: property.type,
        dataGroupId: property.dataGroupId,
        dataGroupName,
        ...(property.customPropertyId ? {customPropertyId: String(property.customPropertyId)} : {}),
        path: [dataGroupName],
        isLookupTableProperty: false,
        isRecentlyUsed: false,
        i18n,
      },
      historicalProperties,
      {
        allowEdit: !hideEditControls && !readonly,
        allowDescriptionEdit: descriptionEditDisabled,
        lookupTables: {},
        iconReferenceType: `dataType`,
      },
    );
    return tooltipData;
  }

  return null;
}

export function createContextPanelEventHandlers({
  track,
  additionalArgs = {},
  availableContexts = {},
  handleInlineEvent,
  handleInlineCohort,
}: Partial<ContextPanelRendererParams> & {
  handleInlineEvent: (...args) => void;
  handleInlineCohort: (...args) => void;
}): Pick<ContextPanelReactProps, `onTooltipContextAction` | `onTooltipContextEditDescription`> {
  const projectId = additionalArgs.projectId;
  const workspaceId = additionalArgs.workspaceId;

  const behaviors = availableContexts.behaviors;
  const metrics = availableContexts.metrics;

  const handleContextPanelAction = async (ev: CustomEvent) => {
    const action = ev.detail.name;
    const item = ev.detail.item;
    if (action === ActionType.ShareFormula || action === ActionType.ShareMetric) {
      const metricId = item.id;
      await promptSharing({
        type: SharableEntityType.Metric,
        id: metricId,
        'project-id': projectId,
      });
    } else if (action === ActionType.ShareBehavior) {
      const behaviorId = item.id;
      await promptSharing({
        type: SharableEntityType.Behavior,
        id: behaviorId,
        'project-id': projectId,
      });
    } else if (action === ActionType.EditCustomEvent) {
      const customEventId = item.id;
      handleInlineEvent(customEventId);
    } else if (action === ActionType.ShareCustomEvent) {
      const {eventName, props} = trackShare.shareButtonClick(ShareButtonUISource.ContextPanel);
      track(eventName, props, false, shareEventPrefix);
      promptSharing({
        type: SharableEntityType.CustomEvent,
        id: item.id,
        'project-id': projectId,
        'is-superadmin': item.superadminOverrideShare,
      });
    } else if (action === ActionType.EditCohort || Boolean(item.value?.cohort?.raw_cohort)) {
      handleInlineCohort(item);
      ev.stopPropagation();
    } else if (action === ActionType.ShareCohort || action === ActionType.ShareCustomProperty) {
      const isShareCohort = action === ActionType.ShareCohort;
      const {eventName, props} = trackShare.shareButtonClick(ShareButtonUISource.ContextPanel);
      track(`[${shareEventPrefix}] ${eventName}`, props);
      promptSharing({
        type: isShareCohort ? SharableEntityType.Cohort : SharableEntityType.CustomProperty,
        id: isShareCohort ? item.id : item.property.customPropertyId,
        'project-id': projectId,
        'is-superadmin': item.superadminOverrideShare,
      });
    } else if (action === ActionType.ViewSampleEvents && item.url) {
      track(TRACKING_EVENTS.SAMPLE_EVENT, {}, false);
      if (currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.EventsSidebar)) {
        openEventsSidebar({url: item.url});
      } else {
        window.open(item.url, `_blank`);
      }
    } else if ((action === ActionType.ViewEntity || action === ActionType.LookupTableInfo) && item.url) {
      window.open(item.url, `_blank`);
    } else {
      throw new Error(`action handler not implemented`);
    }
  };

  const handleContextPanelEditDescription = async ({detail}: EditDescriptionCustomEvent) => {
    const updatedDescription = detail.description;
    const entityType = detail.item.type;

    if (entityType === EntityTypeNameSingular.Event || entityType === EntityTypeNameSingular.CustomEvent) {
      const eventStoreKey = detail.item.name;
      const IdName = detail.item.type === EntityTypeNameSingular.Event ? `id` : `customEventId`;

      const definition = eventDefinitionsStore.value[eventStoreKey];
      const descriptionBeforeUpdate = definition?.description ?? null;
      const count = definition?.count || 0;

      let eventDefinition = {[IdName]: detail.item.id, description: detail.description};
      if (!detail.item.id) {
        eventDefinition = {name: detail.item.name, description: detail.description};
      }
      const updateRequestObj = {
        projectId,
        workspaceId,
        eventDefinition,
      };

      const undoRequestObj = {
        ...updateRequestObj,
        eventDefinition: {...updateRequestObj.eventDefinition, description: descriptionBeforeUpdate},
      };

      await updateEntityDescription({
        entityStore: eventDefinitionsStore,
        newDescription: updatedDescription,
        storeKey: eventStoreKey,
        updateRequestObj,
        undoRequestObj,
        entityType,
        addToast,
        track,
      });
      eventDefinitionsStore.update({
        [eventStoreKey]: {
          ...eventDefinitionsStore.value[eventStoreKey],
          count,
        },
      });
    } else if (entityType === EntityType.Behavior) {
      const behaviorId = detail.item.id;
      try {
        await behaviors.patch(behaviorId as number, {description: updatedDescription});
      } catch {
        addToast({
          message: `Sorry, something went wrong. Please contact support if this problem persists.`,
          type: ToastType.ERROR,
          timeout: null,
        });
      }
    } else if (entityType === EntityType.Metric) {
      const metricId = detail.item.id as string;
      let results: MetricsResultsMap;
      try {
        results = await updateMetric({
          metricId,
          metric: {
            description: updatedDescription,
          },
        });
      } catch {
        results = null;
        showMetricsApiErrorToast();
      }
      if (results) {
        metrics.update(results, {partialUpdate: false});
      }
    }
  };

  return {
    onTooltipContextAction: handleContextPanelAction,
    onTooltipContextEditDescription: handleContextPanelEditDescription,
  };
}
